import { MoveHorizontal, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePipelinePresence } from '@/hooks/use-pipeline-presence';
import { usePipelineContext } from '../../context';

/**
 * Pipeline overview with stats and progress bar
 * Shows total deals, total value, and stage distribution
 */
export function PipelineOverview() {
  const { state, meta } = usePipelineContext();
  const { presence: pipelinePresence } = usePipelinePresence();
  const { viewMode } = state;
  const { pipelineStats } = meta;

  // Don't render in calendar view
  if (viewMode !== 'pipeline') return null;

  return (
    <div className="mb-4 hidden sm:block">
      <Card className="bg-white/80 dark:bg-slate-900/80 border-slate-200/50 dark:border-slate-700/50 shadow-sm">
        <div className="p-2 lg:p-3">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 lg:gap-4">
            {/* Stats Section - Compact and Responsive */}
            <div className="flex items-center gap-2 lg:gap-3 shrink-0">
              <div className="w-6 h-6 lg:w-7 lg:h-7 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                <MoveHorizontal className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-white" />
              </div>
              <div className="flex items-center gap-1">
                <h2 className="text-sm lg:text-base font-semibold text-slate-800 dark:text-slate-200">
                  <span className="hidden md:inline">{pipelineStats.totalDeals} leads</span>
                  <span className="md:hidden">{pipelineStats.totalDeals}</span>
                  <span className="mx-1 text-slate-400">•</span>
                  <span className="text-emerald-600 dark:text-emerald-400">
                    £
                    {pipelineStats.totalValue >= 1000
                      ? `${(pipelineStats.totalValue / 1000).toFixed(1)}K`
                      : pipelineStats.totalValue.toLocaleString('en-GB', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                  </span>
                </h2>
              </div>
            </div>

            {/* Progress Bar - Wider on large screens */}
            <div className="flex-1 lg:max-w-2xl xl:max-w-3xl">
              {/* Desktop version with inline labels */}
              <div className="hidden lg:flex items-center gap-3">
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  Progress
                </span>
                <div className="flex-1 h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden flex shadow-inner">
                  {pipelineStats.stageProgress.map(
                    (stage, index) =>
                      stage.count > 0 && (
                        <Tooltip key={stage.stageId}>
                          <TooltipTrigger asChild>
                            <div
                              className="h-full transition-all duration-300 hover:brightness-110 cursor-pointer relative group"
                              style={{
                                backgroundColor: stage.stageColor,
                                width: `${stage.percentage}%`,
                                borderRight:
                                  index < pipelineStats.stageProgress.length - 1
                                    ? '1px solid rgba(255,255,255,0.2)'
                                    : 'none',
                              }}
                            >
                              {/* Show percentage on hover for segments > 15% */}
                              {stage.percentage > 15 && (
                                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                  {Math.round(stage.percentage)}%
                                </span>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs">
                              <div className="font-semibold">{stage.stageName}</div>
                              <div>
                                {stage.count} leads ({stage.percentage.toFixed(1)}%)
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )
                  )}
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  {pipelineStats.totalDeals} total
                </span>
              </div>

              {/* Tablet/Medium screen version - simplified */}
              <div className="lg:hidden">
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden flex shadow-inner">
                  {pipelineStats.stageProgress.map(
                    (stage, index) =>
                      stage.count > 0 && (
                        <div
                          key={stage.stageId}
                          className="h-full transition-all duration-300"
                          style={{
                            backgroundColor: stage.stageColor,
                            width: `${stage.percentage}%`,
                            borderRight:
                              index < pipelineStats.stageProgress.length - 1
                                ? '1px solid rgba(255,255,255,0.2)'
                                : 'none',
                          }}
                          title={`${stage.stageName}: ${stage.count} leads (${stage.percentage}%)`}
                        />
                      )
                  )}
                </div>
              </div>
            </div>

            {/* Active Users Presence - Only on XL screens */}
            {pipelinePresence.users.length > 0 && (
              <div className="hidden xl:flex items-center gap-2 shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-slate-400" />
                      <div className="flex -space-x-1.5">
                        {pipelinePresence.users.slice(0, 5).map((user) => (
                          <div
                            key={user.userId}
                            className="w-6 h-6 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center text-[10px] font-medium text-white shadow-sm"
                            style={{ backgroundColor: user.color }}
                          >
                            {user.initials}
                          </div>
                        ))}
                        {pipelinePresence.users.length > 5 && (
                          <div className="w-6 h-6 rounded-full border-2 border-white dark:border-slate-800 bg-slate-400 flex items-center justify-center text-[10px] font-medium text-white shadow-sm">
                            +{pipelinePresence.users.length - 5}
                          </div>
                        )}
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <div className="font-semibold mb-1">Active Now</div>
                      {pipelinePresence.users.map((user) => (
                        <div key={user.userId}>{user.name}</div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
